import { useCallback } from 'react';

import { useLocalizationContext } from '@order/Localization';

// ────────────────────────────────────────────────────────────────────────────────

export const useLineItemName = (
  product: Product | null,
  lineItem: LineItem | null,
) => {
  const formatLineItemName = useFormatLineItemName();

  return formatLineItemName(product, lineItem);
};

/**
 * Returns formatted order's line items names string based on their type.
 */
export const useLineItemsNames = (lineItems: readonly LineItem[] = []) => {
  const formatLineItemName = useFormatLineItemName();

  const sortedOrderLineItems = sortOrderLineItemsBasedOnCategory(lineItems);
  const formattedOrderLineItemsNames = sortedOrderLineItems.map((lineItem) =>
    formatLineItemName(lineItem?.product, lineItem),
  );

  return formattedOrderLineItemsNames.join(', ');
};

/**
 * Returns the helper function that returns the line item name,
 * based on the following guidelines and order:
 *
 * 1. Return user-defined custom name if it is set.
 * 2. Return the product name with the "custom" prefix if it was customized.
 * 3. Return product name.
 */
export const useFormatLineItemName = () => {
  const { t } = useLocalizationContext();

  return useCallback(
    (
      product: Product | undefined | null,
      lineItem: LineItem | undefined | null,
    ) => {
      const productName = product?.name ?? lineItem?.product?.name ?? '';
      const customName = lineItem?.customName;
      const isCustomized = Boolean(lineItem?.isCustom && !product?.isCustom);

      // ─────────────────────────────────────────────────────────────────

      if (customName) return customName;

      if (isCustomized)
        return t('general.custom-product-name', { productName });

      return productName;
    },
    [t],
  );
};

// ─── HELPERS ────────────────────────────────────────────────────────────────────

function sortOrderLineItemsBasedOnCategory(lineItems: readonly LineItem[]) {
  return [...lineItems].sort((currentLineItem, nextLineItem) => {
    const shouldMoveLineItemToBeginning =
      checkIfCoreLineItemProduct(currentLineItem) &&
      !checkIfCoreLineItemProduct(nextLineItem);

    return shouldMoveLineItemToBeginning ? -1 : 0;
  });
}

function checkIfCoreLineItemProduct(lineItem: LineItem) {
  const { categoryName } = lineItem.product ?? {};

  return categoryName === 'salads' || categoryName === 'bowls';
}

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type LineItem = {
  readonly customName?: string | null;
  readonly product?: Product;
  readonly isCustom?: boolean;
};

type Product = {
  readonly name?: string;
  readonly categoryName?: string;
  readonly isCustom?: boolean;
};
